import { useEffect, useCallback, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useWalkthrough } from "src/contexts/WalkthroughContext";
import { useStore } from "src/stores/store";
import { WALKTHROUGH_STEPS } from "./walkthrough-steps";
import WalkthroughOverlay from "./WalkthroughOverlay";
import WalkthroughWelcome from "./WalkthroughWelcome";
import WalkthroughTooltip from "./WalkthroughTooltip";
import WalkthroughComplete from "./WalkthroughComplete";

const isSidebarStep = (selector: string | null): boolean =>
  !!selector && selector.startsWith('[data-tour="sidebar-');

const WalkthroughTour: FC = () => {
  const { i18n } = useTranslation();
  const { globalStore } = useStore();
  const {
    isActive,
    currentStep,
    nextStep,
    prevStep,
    skipTour,
    endTour,
  } = useWalkthrough();

  const step = WALKTHROUGH_STEPS[currentStep];
  const isRTL = i18n.language.startsWith("ar");
  const isLastStep = currentStep === WALKTHROUGH_STEPS.length - 1;
  const isMobile = globalStore.isMobileView;

  // Skip sidebar-targeted steps on mobile viewports
  useEffect(() => {
    if (!isActive || !step) return;
    if (isMobile && step.type === "tooltip" && isSidebarStep(step.targetSelector)) {
      const timer = setTimeout(() => {
        nextStep();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, step, isMobile, nextStep]);

  // Handle missing target elements — skip to next step
  useEffect(() => {
    if (!isActive || !step || step.type === "modal") return;
    if (!step.targetSelector) return;
    // Already handled by mobile skip above
    if (isMobile && isSidebarStep(step.targetSelector)) return;

    const target = document.querySelector(step.targetSelector);
    if (!target) {
      const timer = setTimeout(() => {
        nextStep();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, step, isMobile, nextStep]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isActive) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          skipTour();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (isLastStep) {
            endTour();
          } else {
            nextStep();
          }
          break;
        case "ArrowRight":
          e.preventDefault();
          if (isRTL) {
            prevStep();
          } else {
            nextStep();
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (isRTL) {
            nextStep();
          } else {
            prevStep();
          }
          break;
      }
    },
    [isActive, isLastStep, isRTL, nextStep, prevStep, skipTour, endTour],
  );

  useEffect(() => {
    if (!isActive) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isActive, handleKeyDown]);

  if (!isActive || !step) return null;

  return (
    <>
      <WalkthroughOverlay />
      {step.type === "modal" && currentStep === 0 && <WalkthroughWelcome />}
      {step.type === "tooltip" && <WalkthroughTooltip step={step} />}
      {step.type === "modal" && currentStep > 0 && <WalkthroughComplete />}
    </>
  );
};

export default WalkthroughTour;
